/**
 * RUN A — Approved 2026 pricing & membership PRESENTATION layer (PREVIEW ONLY).
 *
 * This module is a DISPLAY-ONLY source of truth for the approved new prices,
 * the three approved memberships and the £95 assessment-credit promotion.
 *
 * IMPORTANT (Run A scope):
 *   - Nothing here changes Stripe, booking prices, subscriptions or payment
 *     logic. The live checkout still charges the CURRENT backend prices.
 *   - Wherever a displayed approved price DIFFERS from the live checkout price,
 *     the public page disables the transactional CTA and shows a non-
 *     transactional "Booking update in progress" label instead (so no customer
 *     can enter checkout at a mismatched price).
 *   - Run B reconciles Stripe / booking / membership entitlements and then
 *     re-enables these CTAs.
 */

// Approved monthly membership prices (display) and the current live checkout
// price they must be reconciled against in Run B.
export const APPROVED_MEM_PRICE = { digital: 59, progress: 169, performance: 279 };
export const LIVE_MEM_CHECKOUT = { digital: 49, progress: 149, performance: 229 };
export const APPROVED_MEMBERSHIP_IDS = ["digital", "progress", "performance"];

// Full approved feature lists (verbatim from the approved brief).
export const APPROVED_MEM_FEATURES = {
  digital: [
    "Personalised digital rehabilitation programme",
    "One monthly remote review (up to 15 minutes)",
    "One monthly programme review/update",
    "Defined non-urgent messaging allowance",
    "No included face-to-face session",
  ],
  progress: [
    "One 45-minute in-person session per month",
    "One 15-minute remote review",
    "Personalised rehabilitation programme",
    "Monthly programme progression",
    "Defined messaging allowance",
  ],
  performance: [
    "Two 45-minute in-person sessions per month",
    "One 15-minute remote review",
    "Rehabilitation and performance programming",
    "Progress monitoring",
    "Quarterly reassessment within the existing session allowance",
    "Defined messaging allowance",
  ],
};

export const APPROVED_MEM_TAGLINE = {
  digital: "Remote rehabilitation support to follow and progress your programme.",
  progress: "Monthly in-person contact plus remote support and progression.",
  performance: "Higher-contact rehabilitation and performance support.",
};

/**
 * À-la-carte service overrides, keyed by the EXACT live service name seeded in
 * the backend. `price` is the approved display price; `liveCheckout` is the
 * current price the checkout still charges (used for mismatch detection and
 * the Run B reconciliation report). `display` renames the row for presentation.
 */
export const SERVICE_OVERRIDES = {
  "Initial Assessment":              { display: "Initial Physiotherapy Assessment", price: 95,  liveCheckout: 90 },
  "Follow-Up Session":               { display: "Physiotherapy Follow-Up",          price: 75,  liveCheckout: 65 },
  "Strength & Conditioning Session": { display: "Strength & Conditioning",          price: 100, liveCheckout: 60 },
  "Home Visit (within London)":      { display: "Home Visits",      quoted: true,   liveCheckout: 90 },
};

/** New approved à-la-carte items not present in the live catalogue (display-only). */
export const NEW_SERVICES = [
  { id: "approved-short-followup", name: "Short Follow-Up",        duration_min: 30, price: 55,  category: "Sport therapy", description: "A shorter 30-minute review or treatment session." },
  { id: "approved-five-followups", name: "Five Follow-Up Sessions", duration_label: "5 × 45 min", price: 350, category: "Sport therapy", description: "A block of five 45-minute physiotherapy follow-up sessions." },
];

/** The £95 Initial Assessment credit promotion (presentation only — NOT active). */
export const ASSESSMENT_CREDIT_PROMO = {
  status: "New patient offer",
  title: "Your £95 Initial Assessment, Credited Towards Your Membership",
  body:
    "Book your initial physiotherapy assessment for £95. If you choose an eligible Progress or Performance membership within seven days of your assessment, we'll credit the full £95 assessment fee towards your first month.",
  eligibility: [
    "New patients only",
    "Progress or Performance membership",
    "One credit per eligible patient",
    "Assessment must be completed before redemption",
    "Membership must be clinically appropriate",
    "No overlapping discounts",
  ],
  examples: [
    { plan: "Progress", firstMonth: 74, recurring: 169 },
    { plan: "Performance", firstMonth: 184, recurring: 279 },
  ],
};

/**
 * Apply the approved pricing presentation to the live `/services/public`
 * categories. Returns a NEW array; never mutates the API response in place.
 * Each affected service is tagged so the row can render a disabled,
 * non-transactional CTA when the price differs from checkout.
 */
export function applyApprovedServicePricing(categories) {
  // Run B: the backend catalogue is now the source of truth (approved prices,
  // renamed services, the two new items and the quoted Home Visits all come
  // straight from the API), so this is a pass-through. Kept for import stability.
  return categories || [];
}

/** Mismatch report rows for the Run B reconciliation checklist. */
export function priceMismatchReport() {
  const rows = [];
  for (const [name, ov] of Object.entries(SERVICE_OVERRIDES)) {
    if (ov.quoted) rows.push({ item: ov.display, display: "Individually quoted", checkout: `£${ov.liveCheckout}` });
    else if (ov.price !== ov.liveCheckout) rows.push({ item: ov.display, display: `£${ov.price}`, checkout: `£${ov.liveCheckout}` });
  }
  for (const id of APPROVED_MEMBERSHIP_IDS) {
    if (APPROVED_MEM_PRICE[id] !== LIVE_MEM_CHECKOUT[id])
      rows.push({ item: `${id} membership`, display: `£${APPROVED_MEM_PRICE[id]}/mo`, checkout: `£${LIVE_MEM_CHECKOUT[id]}/mo` });
  }
  return rows;
}
